import { useEffect, useState, useMemo } from 'react';
import TreeNode from './TreeNode';
import GithubConnector from './GithubConnector';
import { useFile } from '../../context/FileContext';
import { buildFileTree } from '../../utils/treeBuilder';
import { processDroppedItems, processUploadedFiles } from '../../utils/fileSystem';
import GitHubClient from '../../api/GitHubClient';
import '../../index.css';

/**
 * File explorer component - manages local and GitHub file sources
 */
const FileExplorer = () => {
  const {
    githubBranch,
    githubNameAndRepo,
    githubAccessToken,
    source,
    setSource,
    localFiles,
    setLocalFiles,
    clearLocalFiles,
    focusedFilePath,
    setFocusedFilePath,
    selectedFilePath
  } = useFile();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.items) {
      const droppedFiles = await processDroppedItems(e.dataTransfer.items);
      setLocalFiles(prev => [...prev, ...droppedFiles]);
      setSource('local');
    }
  };

  useEffect(() => {
    if (!githubNameAndRepo || !githubBranch) return;
    // Switch to github source when loading from GitHub
    if (focusedFilePath) {
      setSource('github');
    }
    fetchTree();
  }, [githubNameAndRepo, githubBranch, githubAccessToken, focusedFilePath]);

  const fetchTree = async () => {
    try {
      const githubClient = new GitHubClient(githubAccessToken);
      const fileTree = await githubClient.getFileTree(githubNameAndRepo, githubBranch);
      setFiles(fileTree);
    } catch (error) {
      console.error('Error fetching file tree:', error);
    }
  };

  const handleLocalUpload = (filesList) => {
    const filesArray = processUploadedFiles(filesList);
    setLocalFiles(filesArray);
    setSource('local');
  };

  const activeFiles = source === 'local' ? localFiles : files;
  const treeData = useMemo(() => buildFileTree(activeFiles), [activeFiles]);

  return (
    <div className="FileExplorer">
      <div className="app-brand">
        <a href="/" className="app-name">
          viewer.HelloTwinCAT.dev
        </a>
        <a href="https://hellotwincat.dev" className="app-subtitle" target="_blank" rel="noopener noreferrer">
          TwinCAT Tutorials
        </a>
      </div>

      <div className="source-selector">
        <button
          onClick={() => setSource('github')}
          className={source === 'github' ? 'active' : ''}
        >
          GitHub
        </button>
        <button
          onClick={() => setSource('local')}
          className={source === 'local' ? 'active' : ''}
        >
          Local Folder
        </button>
      </div>

      {source === 'github' ? (
        <GithubConnector />
      ) : (
        <>
          <h1>Upload Local Folder</h1>
          <p>
            When using drag and drop, folders are appended, so you can add multiple folders this
            way. Also drag and drop can add single files.
          </p>
          <div
            className={`local-uploader ${isDragging ? 'dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
          >
            <p>
              Drag & Drop <b>folder</b> here
            </p>
            <span>or select with dialog:</span>
            <input
              type="file"
              webkitdirectory="true"
              directory=""
              onChange={(e) => handleLocalUpload(e.target.files)}
            />
          </div>
          {localFiles.length > 0 && (
            <div className="clear-container">
              <button onClick={clearLocalFiles} className="clear-btn">
                Clear All
              </button>
            </div>
          )}
        </>
      )}
      <hr style={{ margin: '20px 0' }} />
      <h1>Files</h1>
      <div>{treeData.map(node => <TreeNode key={node.name} node={node} focusedFilePath={focusedFilePath} selectedFilePath={selectedFilePath} onFocusCleared={() => setFocusedFilePath(null)} />)}</div>
      <hr />
      <div>
        <h3>About</h3>
        <p>
          Supports .TcPOU, .TcGVL, .TcDUT, .TcTLEO, .TcIO files, reading out declarations and
          implementations, methods and properties.
        </p>
        <p>
          Also can read events from .tmc files. But may not load files if they are too long...
          lets say when you stuff all events into main tmc file.
        </p>
      </div>
    </div>
  );
};

export default FileExplorer;
